import { FlowPrompt, Typography } from '@capture/capture-components'
import {
    type BackendControlledFullScreenCardDTO,
    FullscreenCardIllustrationDTO,
    WebActionTypeDTO,
    type CTActionDTO,
} from '@capture/client-api/src/orval'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { dismissFullScreenCard } from '~/API/currentUser'
import backupIllustration from '~/assets/illustrations/backend-backup.svg'
import infoIllustration from '~/assets/illustrations/backend-info.svg'
import photosIllustration from '~/assets/illustrations/backend-photos.svg'
import subscriptionIllustration from '~/assets/illustrations/backend-subscription.svg'
import warningIllustration from '~/assets/illustrations/backend-warning.svg'
import { mediaQueries } from '~/assets/styleConstants'

interface FullScreenCardProps {
    card: BackendControlledFullScreenCardDTO
}

export const FullScreenCard = ({
    card: {
        card_id,
        client_values: {
            title,
            illustration,
            body,
            primary_cta,
            secondary_cta,
        },
    },
}: FullScreenCardProps) => {
    const navigate = useNavigate()
    const dispatch = useDispatch()

    /**
     * When a user presses one of the CTAs
     * we need to update the backend on the new state
     * and handle the action as described by the web_action payload
     */
    const handleAction = async (action: CTActionDTO) => {
        if (action.dismiss_on_tap) {
            await dismissFullScreenCard(card_id, dispatch)
        }

        switch (action.web_action.action_type) {
            case WebActionTypeDTO.internal_navigation:
                navigate(action.web_action.url)
                return
            case WebActionTypeDTO.new_tab:
                window.open(
                    action.web_action.url,
                    '_blank',
                    'noopener,noreferrer',
                )
                return
            case WebActionTypeDTO.redirect:
                window.location.replace(action.web_action.url)
                return
        }
    }

    return (
        <FlowPrompt.Root
            open
            onOpenChange={(open) => {
                if (!open) {
                    dismissFullScreenCard(card_id, dispatch)
                }
            }}>
            <FlowPrompt.Content title={title} preventCloseOnClickOutside>
                {illustration && (
                    <FullScreenCardIllustration illustration={illustration} />
                )}
                <Typography.Paragraph>{body}</Typography.Paragraph>
                <FlowPrompt.ButtonsSection>
                    <FlowPrompt.ActionButton
                        type="button"
                        variant="primary"
                        key="primary"
                        closeOnSubmit={false}
                        onPress={() => handleAction(primary_cta)}>
                        {primary_cta.label}
                    </FlowPrompt.ActionButton>
                    {secondary_cta && (
                        <FlowPrompt.ActionButton
                            type="button"
                            variant="secondary"
                            key="secondary"
                            closeOnSubmit={false}
                            onPress={() => handleAction(secondary_cta)}>
                            {secondary_cta.label}
                        </FlowPrompt.ActionButton>
                    )}
                </FlowPrompt.ButtonsSection>
            </FlowPrompt.Content>
        </FlowPrompt.Root>
    )
}

const FullScreenIllustration = styled.img`
    margin-inline: auto;
    width: 180px;

    ${mediaQueries.mobile} {
        width: 100px;
    }
`

interface FullScreenCardIllustrationProps {
    illustration: FullscreenCardIllustrationDTO
}

const FullScreenCardIllustration = ({
    illustration,
}: FullScreenCardIllustrationProps) => {
    switch (illustration) {
        case FullscreenCardIllustrationDTO.backup:
            return (
                <FullScreenIllustration src={backupIllustration} alt="backup" />
            )
        case FullscreenCardIllustrationDTO.info:
            return <FullScreenIllustration src={infoIllustration} alt="info" />
        case FullscreenCardIllustrationDTO.photos:
            return (
                <FullScreenIllustration src={photosIllustration} alt="photos" />
            )
        case FullscreenCardIllustrationDTO.subscriptions:
            return (
                <FullScreenIllustration
                    src={subscriptionIllustration}
                    alt="subscriptions"
                />
            )
        case FullscreenCardIllustrationDTO.warning:
            return (
                <FullScreenIllustration
                    src={warningIllustration}
                    alt="warning"
                />
            )
        default:
            return null
    }
}
